/** @file validateDoc rules for mounted attachments (§5.9): handle, not skinned, slot on the socket bone. */
import type { Doc } from './schema';
import type { AttachmentDoc } from './schemaParts/attachments';
import { makeError, type Pose2DError } from '../pose2dError';

/** The first §5.9 rule a mounted attachment breaks, or null. */
function mountProblem(doc: Doc, attId: string, att: AttachmentDoc, socketBone: string): string | null {
  if (!att.handle) return 'a mounted attachment needs a handle';
  if (att.type === 'mesh' && att.geometry.kind === 'skinned') return 'skinned meshes cannot be mounted';
  const bones = Object.values(doc.skins).flatMap((s) => s.entries).filter((e) => e.attachment === attId).map((e) => doc.slots[e.slot]?.bone);
  return bones.every((bone) => bone === undefined || bone === socketBone) ? null : "the attachment's slot must sit on the socket bone";
}

/** Appends MOUNT_INVALID for mounted attachments that break §5.9 (a missing socket is NOT_FOUND elsewhere). */
export function checkDocMounts(doc: Doc, out: Pose2DError[]): void {
  for (const [id, att] of Object.entries(doc.attachments)) {
    const socket = att.mount && doc.sockets[att.mount.socket];
    if (!socket) continue;
    const reason = mountProblem(doc, id, att, socket.bone);
    if (reason !== null) out.push(makeError('MOUNT_INVALID', { attachment: att.name, reason }, { path: `attachments.${id}.mount` }));
  }
}
